import { useContext, useEffect, useState } from "react";

import "./manager-config.scss";
import { Button } from "react-bootstrap";
import { BsArrowLeft, BsSave } from "react-icons/bs";

import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import Spinner from "../../../shared/components/spinner/spinner";
import TextInput from "../../../shared/components/text-input/text-input";
import AuthContext from "../../../shared/context/auth-context";
import { IEnumerationOption } from "../../../shared/definitions/datatypes";
import useHttp from "../../../shared/hooks/use-http";
import { useHttpGet } from "../../../shared/hooks/use-httpNonTable";
import { failedToast, getAllData, getData, postSingle } from "../../../shared/utils/api";
import { useBack } from "../../../shared/utils/use-back";
import { usePrompt } from "../../../shared/utils/use-prompt";
import { createUUID, IObject, validateLoadingStatusNew } from "../../../shared/utils/utils";
import { ITableRowData } from "../../components/data-table/data-table";
import SearchInput from "../../components/search-input/search-input";

const ManagerConfig = () => {
  const { handleBack } = useBack();

  const authCtx = useContext(AuthContext);

  const { sendRequest: getManagerConfig, status: getManagerConfigStatus, data: getManagerConfigData, error: errorGetManagerConfig } = useHttpGet(getData, false, authCtx.token, undefined, "manager");
  const { sendRequest: getAlertcases, status: getAlertcasesStatus, data: getAlertcasesData, error: errorGetAlertcases } = useHttp(getAllData, false, authCtx.token, undefined, "platform");
  const { sendRequest: postConfig } = useHttp(postSingle, false, authCtx.token, "managerConfig", "manager");

  const [alertcases, setAlertCases] = useState<IEnumerationOption[]>([]);
  const [configData, setConfigData] = useState<ITableRowData>({ 
    id: "", 
    suspicious_definition: "", 
    suspicious_positive: "", 
    suspicious_negative: "" 
  });
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { flag, setFlag, next } = usePrompt(isDirty);

  useEffect(() => {
    getManagerConfig("configuration");
    getAlertcases("datamodel/alertcases");
  }, []);

  useEffect(() => {
    if (getManagerConfigStatus === "completed" && getManagerConfigData && !errorGetManagerConfig) {
      setConfigData(getManagerConfigData);
    }
  }, [getManagerConfigData, getManagerConfigStatus, errorGetManagerConfig]);

  useEffect(() => {
    if (getAlertcasesData && !errorGetAlertcases && getAlertcasesStatus === "completed") {
      setAlertCases(getAlertcasesData.rows.map((alertcase: IObject) => ({
        id: alertcase.id,
        name: alertcase.name
      })));
    }
  }, [getAlertcasesData, getAlertcasesStatus, errorGetAlertcases]);

  const handleAlertCaseChange = (selectedOption: IEnumerationOption | undefined) => {
    if (!selectedOption) {
      setConfigData({ ...configData, id: "", manager_alertcase_id: "" });
    } else {
      setConfigData({ ...configData, manager_alertcase_id: selectedOption.id });
      if (selectedOption.id !== getManagerConfigData?.manager_alertcase_id) {
        setIsDirty(true);
      } else {
        setIsDirty(false);
      }
    }
  };

  const handleSuspiciousConfigChange = (value: string, inputKey: string) => {
    const newConfigData = { ...configData, [inputKey]: value };
    setConfigData(newConfigData);
    
    const originalData = getManagerConfigData || {};
    const hasChanges = Object.keys(newConfigData).some(key => 
      newConfigData[key] !== originalData[key]
    );
    
    setIsDirty(hasChanges);
  };

  const handleSave = () => {
    if (!configData.manager_alertcase_id) {
      failedToast("Required field Manager AlertCase not filled in");
      return;
    }

    const id = configData.id || createUUID();
    const dataToSave = { 
      id, 
      manager_alertcase_id: configData.manager_alertcase_id,
      suspicious_definition: configData.suspicious_definition,
      suspicious_positive: configData.suspicious_positive,
      suspicious_negative: configData.suspicious_negative
    };
    
    setConfigData({ ...configData, id });
    setIsDirty(false);
    postConfig("configuration", dataToSave);
  };

  return <div className="managerConfig">
    <div onClick={() => handleBack()} className="listPage-nav-btn">
      <BsArrowLeft />
    </div>
    <h1>Manager Configuration</h1>
    <div className="managerConfig-form">
      {validateLoadingStatusNew([getAlertcasesStatus, getManagerConfigStatus]) && <Spinner />}
      
      <SearchInput
        label="Manager AlertCase extension"
        inputKey="managerAlertCase"
        required={true}
        data={alertcases}
        selectedOption={configData.manager_alertcase_id}
        handleOnChange={handleAlertCaseChange}
      />
      
      <TextInput
        type="text"
        id="suspicious_definition"
        required={false}
        inputKey="suspicious_definition"
        autofocus={false}
        placeholder="Review result"
        controlled={true}
        value={configData.suspicious_definition || ""}
        handleOnChange={handleSuspiciousConfigChange}
        label="Review result"
      />
      
      <TextInput
        type="text"
        id="suspicious_positive"
        required={false}
        inputKey="suspicious_positive"
        autofocus={false}
        placeholder="Review result positive label"
        controlled={true}
        value={configData.suspicious_positive || ""}
        handleOnChange={handleSuspiciousConfigChange}
        label="Review result positive label"
      />
      
      <TextInput
        type="text"
        id="suspicious_negative"
        required={false}
        inputKey="suspicious_negative"
        autofocus={false}
        placeholder="Review result negative label"
        controlled={true}
        value={configData.suspicious_negative || ""}
        handleOnChange={handleSuspiciousConfigChange}
        label="Review result negative label"
      />
      
      <div className="managerConfig-buttonsGroup">
        <Button className="managerConfig-save" disabled={!configData.manager_alertcase_id}>
          <BsSave onClick={handleSave} />
        </Button>
      </div>
    </div>
    
    {flag && <ConfirmationPopup
      show={flag}
      handleSaveClick={handleSave}
      handlePopupClosed={() => { setFlag(false); }}
      handleContinue={() => { next(); setFlag(false); }} />}
  </div>;
};

export default ManagerConfig;